/**
 * Negative-PV / inactive-well liability schedule (D3.6 / SPEC-89).
 * Separate from operating PV — never floors negative wells to zero in package totals.
 */

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// Word boundaries so "stage", "partial", and "path" are not inactive wells.
const INACTIVE_STATUS = /\b(inactive|shut-in|shut|ta|p&a|pa)\b/i;

export function inactiveFlag(asset) {
  if (!asset) return false;
  if (asset.inactive === true || asset.shutIn === true || asset.shut_in === true) return true;
  const status = String(asset.status || asset.opsStatus || asset.wellStatus || '');
  if (!status) return false;
  return INACTIVE_STATUS.test(status);
}

/**
 * Build a liability schedule from package assets (+ optional evaluateDeal asset rows).
 *
 * @param {object} pkg DealPackage
 * @param {{ evaluatedAssets?: Array, aroMM?: number }} [opts]
 */
export function buildLiabilitySchedule(pkg, opts = {}) {
  const assets = pkg?.assets || [];
  const evaluated = opts.evaluatedAssets || [];
  const evalByProp = new Map(evaluated.map((a) => [a.propnum, a]));
  const aroMM = num(opts.aroMM) ?? num(pkg?.assumptions?.aroMM) ?? 0;

  const candidates = assets.filter((a) => {
    const oneline = num(a.ref?.pv10);
    return (oneline != null && oneline < 0) || inactiveFlag(a);
  });

  const negAbsSum = candidates.reduce((s, a) => {
    const oneline = num(a.ref?.pv10);
    return s + (oneline != null && oneline < 0 ? Math.abs(oneline) : 0);
  }, 0);

  const rows = candidates.map((a) => {
    const onelinePv = num(a.ref?.pv10);
    const ev = evalByProp.get(a.propnum);
    const modelAssetPv = num(ev?.assetPv);
    const absOneline = onelinePv != null && onelinePv < 0 ? Math.abs(onelinePv) : 0;
    const share =
      aroMM > 0 && negAbsSum > 0 && absOneline > 0 ? (absOneline / negAbsSum) * aroMM : null;
    const inactive = inactiveFlag(a);
    return {
      propnum: a.propnum,
      lease: a.lease || a.propnum,
      onelinePv,
      modelAssetPv,
      suggestedPlugAroShareMM: share == null ? null : +share.toFixed(4),
      inactive,
      regulatoryNote:
        onelinePv != null && onelinePv < 0
          ? inactive
            ? 'Negative oneline PV + inactive/shut-in flag — Texas inactive-well / P&A review'
            : 'Negative oneline PV retained in package totals (not floored). Review P&A / inactive-well exposure.'
          : 'Inactive/shut-in flag without negative oneline PV — confirm regulatory status.',
      classification: {
        onelinePv: 'Fact',
        modelAssetPv: 'Model',
        suggestedPlugAroShareMM: 'Judgment',
        regulatoryNote: 'Judgment',
      },
    };
  });

  rows.sort((a, b) => (a.onelinePv ?? 0) - (b.onelinePv ?? 0));

  const onelineNegativeTotal = rows.reduce(
    (s, r) => s + (r.onelinePv != null && r.onelinePv < 0 ? r.onelinePv : 0),
    0,
  );
  const modelNegativeTotal = rows.reduce(
    (s, r) => s + (r.modelAssetPv != null && r.modelAssetPv < 0 ? r.modelAssetPv : 0),
    0,
  );

  return {
    schemaVersion: 'liabilitySchedule.v1',
    rowCount: rows.length,
    onelineNegativeTotal: +onelineNegativeTotal.toFixed(2),
    modelNegativeTotal: +modelNegativeTotal.toFixed(2),
    floorsNegatives: false,
    note: 'Liability schedule is separate from operating PV. Negative wells are not floored to zero in package totals and are not netted into an opaque PV adjustment.',
    rows,
  };
}

/** Compact freeze snapshot (hashable). */
export function pickLiabilitySnapshot(schedule) {
  if (!schedule) return null;
  return {
    schemaVersion: schedule.schemaVersion,
    rowCount: schedule.rowCount,
    onelineNegativeTotal: schedule.onelineNegativeTotal,
    modelNegativeTotal: schedule.modelNegativeTotal,
    floorsNegatives: false,
    rows: (schedule.rows || []).map((r) => ({
      propnum: r.propnum,
      lease: r.lease,
      onelinePv: r.onelinePv,
      modelAssetPv: r.modelAssetPv == null ? null : +Number(r.modelAssetPv).toFixed(2),
      suggestedPlugAroShareMM: r.suggestedPlugAroShareMM,
      inactive: !!r.inactive,
      regulatoryNote: r.regulatoryNote || '',
    })),
  };
}
